import { appClient } from 'src/config/apollo';
import { useRemoveProviderMutation } from 'src/modules/providers/documents/removeProviderMutation.generated';
import { capture } from 'src/shared/helpers/sentryCapture';
import { useMatchedProvidersMutation } from 'src/modules/providers/documents/matchedProviders.generated';
import * as Sentry from '@sentry/browser';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'src/config/rootStore';
import { getMyCoaches, getMyTherapists } from 'src/modules/providers/selectors/providersSelectors';
import { CareOption, CoachNode } from 'src/generated/webapp_gql';
import { toastNotificationsService } from 'src/config/rootService';
import { config } from 'src/config/config';
import { useTranslation } from 'react-i18next';
import { useChooseProviderMutation } from 'src/modules/providers/documents/chooseProviderMutation.generated';
import { useDoesUserQualifyForTherapy } from 'src/modules/user/hooks/userHooks';

interface useProviderProps {
    providerType?: CareOption;
}

export const useProvider = ({ providerType }: useProviderProps = {}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<Dispatch>();
    const [chooseProviderMutation] = useChooseProviderMutation({ client: appClient });
    const doesUserQualifyForTherapy = useDoesUserQualifyForTherapy();

    const [removeProviderMutation] = useRemoveProviderMutation({
        client: appClient,
    });
    const [matchedProviders, { data, loading }] = useMatchedProvidersMutation({
        client: appClient,
    });

    const myCoaches = useSelector(getMyCoaches);
    const myTherapists = useSelector(getMyTherapists);

    const fetchProviders = async (careRecommendationUuid: string, races: string[]) => {
        try {
            const res = await matchedProviders({
                variables: {
                    careRecommendationUuid,
                    pagination: {
                        pageNumber: 1,
                        pageSize: 9,
                    },
                    preferences: {
                        races,
                    },
                    providerTypeOverride: providerType,
                },
            });

            if (!res.data?.matchedProviders?.providerMatchAttempt?.uuid) {
                Sentry.withScope(scope => {
                    scope.setExtra('error_info', {
                        message: 'No provider match attempt uuid',
                        data: res.data,
                    });
                });

                Sentry.captureException('Failed to fetch providers');
            }

            return res?.data;
        } catch (e) {
            Sentry.withScope(scope => {
                scope.setExtra('error_info', 'Error: failed to fetch matchedProviders');

                capture(e);
            });

            return null;
        }
    };

    const addProvider = async (providerUuid: string, uuid: string): Promise<boolean> => {
        try {
            await chooseProviderMutation({
                variables: {
                    providerUuid,
                    uuid,
                },
            });

            return true;
        } catch (error) {
            capture(error);

            if (`${error}`.includes('no longer has client capacity.')) {
                toastNotificationsService.renderErrorToast(
                    t('providers:mental_health_coach.notification.capacity'),
                );
            } else {
                toastNotificationsService.renderErrorToast(config.messages.genericError);
            }

            return false;
        }
    };

    const removeProvider = async (providerUuid: string): Promise<boolean> => {
        try {
            await removeProviderMutation({
                variables: { providerUuid },
            });

            return true;
        } catch (e) {
            capture(e);
            return false;
        }
    };

    const fetchMyProviders = async () => {
        return dispatch.providers.fetchMyProviders();
    };

    // ProviderSelection component require matchedPreferenceProviders and notMatchedPreferenceProviders
    // after we move all components to use new hook we can refact
    return {
        fetchProviders,
        addProvider,
        removeProvider,
        fetchMyProviders,
        matchedPreferenceProviders:
            (data?.matchedProviders?.providersMatchedPreferences as CoachNode[]) || [],
        notMatchedPreferenceProviders:
            (data?.matchedProviders?.providersDidNotMatchPreferences as CoachNode[]) || [],
        myCoaches,
        myTherapists,
        isMatchedProvidersLoading: loading,
        allowsFiltering: data?.matchedProviders?.allowsFiltering,
        matchedAttempt: data?.matchedProviders?.providerMatchAttempt?.uuid ?? '',
        numberOfCoachesPerMember: 2,
        doesUserQualifyForTherapy,
    };
};
